import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Textarea,
  useToast,
  Select,
} from '@chakra-ui/react';
import ReactJson from 'react-json-view';
import { useUser } from '@clerk/clerk-react';
import { Spinner} from '@chakra-ui/react';

const ResourceListGenerator = () => {
  const { user } = useUser();
  const [task, setTask] = useState('');
  const [expandedTask, setExpandedTask] = useState('');
  const [resourceList, setResourceList] = useState({});
  const [websocket, setWebsocket] = useState(null);
  const [newResource, setNewResource] = useState({ name: '', URL: '', description: '' });
  const [resourceType, setResourceType] = useState('MUST_HAVE_Resources');
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const wsUrl = process.env.REACT_APP_WS_URL;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const ws = new WebSocket(wsUrl);
    setWebsocket(ws);

    ws.onmessage = (event) => {
      setIsLoading(false);
      const data = JSON.parse(event.data);
      switch (data.type) {
        case 'expanded_query':
          setExpandedTask(data.output);
          break;
        case 'resource_list':
          const cleanedData = data.output.replace(/```json\n/, '').replace(/\n```/, '').trim();
          try {
            const parsedList = JSON.parse(cleanedData);
            setResourceList(parsedList);
          } catch (error) {
            console.error('Error parsing JSON:', error);
            toast({
              title: 'Parsing Error',
              description: 'There was an error parsing the resource list data.',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          }
          break;
        default:
          console.error('Unknown message type:', data.type);
      }
    };

    ws.onopen = () => {
      setIsLoading(false);
      toast({
        title: 'Connected',
        description: 'WebSocket connection established',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    };

    ws.onclose = () => {
      toast({
        title: 'Disconnected',
        description: 'WebSocket connection closed',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    };

    return () => ws.close();
  }, [wsUrl, toast]);

  const handleExpandQuery = (e) => {
    e.preventDefault();
    setIsLoading(true); // Start loading
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      const message = `expand_query ${JSON.stringify({ task, userID: user.id })}`;
      websocket.send(message);
    } else {
      setIsLoading(false);
      toast({
        title: 'Error',
        description: 'WebSocket is not connected. Cannot request expanded query.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleGenerateResourceList = () => {
    setIsLoading(true); // Start loading
    const finalTask = expandedTask || task;
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      const message = `start ${JSON.stringify({ task: finalTask, report_type: 'resource_report', userID: user.id })}`;
      websocket.send(message);
    } else {
      setIsLoading(false); // Stop loading
      toast({
        title: 'Connection Error',
        description: 'WebSocket connection is not established.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAddResource = () => {
    const updatedResources = {
      ...resourceList,
      [resourceType]: [...(resourceList[resourceType] || []), newResource],
    };
    setResourceList(updatedResources);
    setNewResource({ name: '', URL: '', description: '' });
  };

  const handleSaveResourceList = async () => {
    // Convert the resource list object to a JSON string
    const agentName = resourceList.agent_name;
    console.log('Saving resource list for agent:', agentName);
    const resourceListJson = JSON.stringify(resourceList, null, 2);
  
    // Create a Blob from the JSON string
    const blob = new Blob([resourceListJson], { type: 'application/json' });
  
    // Create a FormData object and append the file and agent name
    const formData = new FormData();
    formData.append('file', blob, `${agentName}_resource_list.json`); // Prepend the agent name to the filename
    formData.append('agentName', agentName); // Also send the agent name as a separate field
  
    try {
      const response = await fetch(`${backendUrl}/save_resource_list`, {
        method: 'POST',
        body: formData, // Send the FormData object as the request body
      });
  
      if (!response.ok) {
        throw new Error('Failed to save the resource list');
      }
  
      const result = await response.json();
      toast({
        title: 'Success',
        description: 'Resource list saved successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'There was an error saving the resource list',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error('Error saving the resource list:', error);
    }
  };
  
  

  return (
    <Box p={5}>
      <VStack spacing={4}>
      {isLoading && (
        <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
      )}
        <FormControl>
          <FormLabel>Enter your task</FormLabel>
          <Input value={task} onChange={(e) => setTask(e.target.value)} placeholder="Your task here" />
        </FormControl>
        <Button colorScheme="blue" onClick={handleExpandQuery}>Expand Task</Button>
        <Textarea value={expandedTask} onChange={(e) => setExpandedTask(e.target.value)} placeholder="Expanded task will appear here" />
        <Button colorScheme="teal" onClick={handleGenerateResourceList}>Generate Resource List</Button>
        
        <Select value={resourceType} onChange={(e) => setResourceType(e.target.value)}>
          <option value="MUST_HAVE_Resources">MUST HAVE Resources</option>
          <option value="MUST_NOTS_Resources">MUST NOTS Resources</option>
        </Select>

        <VStack spacing={4} align="stretch">
          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input value={newResource.name} onChange={(e) => setNewResource({ ...newResource, name: e.target.value })} placeholder="Resource name" />
          </FormControl>
          <FormControl>
            <FormLabel>URL</FormLabel>
            <Input value={newResource.URL} onChange={(e) => setNewResource({ ...newResource, URL: e.target.value })} placeholder="Resource URL" />
          </FormControl>
          <FormControl>
            <FormLabel>Description</FormLabel>
            <Input value={newResource.description} onChange={(e) => setNewResource({ ...newResource, description: e.target.value })} placeholder="Resource description" />
          </FormControl>
          <Button colorScheme="green" onClick={handleAddResource}>Add New Resource</Button>
        </VStack>

        <Box width="100%" p={4} overflow="auto" bg="gray.50" borderRadius="md" borderColor="gray.200" borderWidth="1px">
          <ReactJson src={resourceList} name={false} theme="rjv-default" collapsed={false} enableClipboard={false} onEdit={(edit) => {
            setResourceList(edit.updated_src);
          }} onAdd={(add) => {
            setResourceList(add.updated_src);
          }} onDelete={(del) => {
            setResourceList(del.updated_src);
          }} />
        </Box>

        <Button colorScheme="purple" onClick={handleSaveResourceList}>Save Resource List</Button>
      </VStack>
    </Box>
  );
};

export default ResourceListGenerator;
